import React, { memo, useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  NativeSelect,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PrintTwoToneIcon from "@mui/icons-material/PrintTwoTone";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import configServ from "../../../services/config";
import SearchBox from "../../Search/SearchBox";
import useDebounce from "../../hooks/useDebounce";
import LoaderNew from "../../Loader/LoaderNew";
import { formatDispatchDate, formatDispatchDateRange } from "../../../utility/dispatchScheduleDates";

const headerCellStyle = {
  fontSize: 14,
  fontWeight: "bold",
  backgroundColor: "#4CB5F5",
  color: "white",
};

const subHeaderCellStyle = {
  fontSize: 13,
  fontWeight: "bold",
  backgroundColor: "#B3DFFB",
  color: "#1a1a1a",
};

const openPdfFromResponse = (response, fallbackName = "dispatch_schedule.pdf") => {
  if (response?.status === 200 && response?.pdf) {
    const linkSource = `data:application/pdf;base64,${response.pdf}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.target = "_blank";
    downloadLink.download = response.file_name || fallbackName;
    downloadLink.click();
    return true;
  }
  return false;
};

const DispatchScheduleTable = ({ isChanged }) => {
  const { company_id } = useSelector((state) => state.GlobalVariables);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchBy, setSearchBy] = useState("customer_name");
  const [search, setSearch] = useState("");
  const debounceSearch = useDebounce(search, 800);
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("id");
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const listParams = {
    company_id,
    page,
    per_page: rowsPerPage,
    sort_by: orderBy,
    sort_order: order,
    search_by: searchBy,
    search: debounceSearch || "",
    start_date: filterStartDate || null,
    end_date: filterEndDate || null,
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await configServ.getDispatchScheduleList(listParams);

      if (res.status === 200) {
        setRows(res.data || []);
        setCount(res.total || 0);
      } else {
        setRows([]);
        setCount(0);
      }
    } catch (e) {
      console.log(e);
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (company_id) {
      fetchList();
    }
  }, [company_id, isChanged, page, rowsPerPage, order, orderBy, debounceSearch, filterStartDate, filterEndDate, searchBy]);

  useEffect(() => {
    setPage(0);
  }, [debounceSearch, filterStartDate, filterEndDate, searchBy]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleToggleRow = (rowId) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleRowPrint = async (row) => {
    try {
      setExporting(true);
      const res = await configServ.printDispatchSchedule({
        company_id,
        id: row.id,
      });
      if (openPdfFromResponse(res)) {
        toast.success("Dispatch schedule opened for print.");
      } else {
        toast.error(res?.message || "Failed to print dispatch schedule.");
      }
    } catch (e) {
      console.log(e);
      toast.error("Failed to print dispatch schedule.");
    } finally {
      setExporting(false);
      handleMenuClose();
    }
  };

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      const res = await configServ.exportDispatchSchedulePdf({
        company_id,
        search_by: searchBy,
        search: debounceSearch || "",
        start_date: filterStartDate || null,
        end_date: filterEndDate || null,
      });
      if (openPdfFromResponse(res, "DispatchSchedule_Report.pdf")) {
        toast.success("PDF exported successfully.");
      } else {
        toast.error(res?.message || "Failed to export PDF.");
      }
    } catch (e) {
      console.log(e);
      toast.error("Failed to export PDF.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const res = await configServ.getDispatchScheduleList({
        company_id,
        page: 0,
        per_page: count || 10000,
        sort_by: orderBy,
        sort_order: order,
        search_by: searchBy,
        search: debounceSearch || "",
        start_date: filterStartDate || null,
        end_date: filterEndDate || null,
      });

      const allRows = res.status === 200 ? res.data || [] : [];

      if (!allRows.length) {
        toast.error("No records to export.");
        return;
      }

      const exportRows = [];
      allRows.forEach((schedule) => {
        (schedule.items || []).forEach((item) => {
          exportRows.push({
            "Schedule No.": schedule.schedule_no || "",
            Customer: schedule.customer_name || "",
            "Start Date": formatDispatchDate(schedule.start_date),
            "End Date (Target)": formatDispatchDate(schedule.end_date),
            "Date Range": formatDispatchDateRange(schedule.start_date, schedule.end_date),
            "PO No.": item.order_no || "",
            "Item Name": item.itemname || "",
            "Item Code": item.item_code || "",
            Quantity: item.qty || "",
            Rate: item.rate || "",
            Amount: Number(item.qty || 0) * Number(item.rate || 0),
            "Created At": schedule.created_at
              ? new Date(schedule.created_at).toLocaleDateString()
              : "",
          });
        });
      });

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Dispatch Schedule");
      XLSX.writeFile(workbook, "dispatch_schedule_report.xlsx");
      toast.success("Excel exported successfully.");
    } catch (e) {
      console.log(e);
      toast.error("Failed to export Excel.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Dispatch Schedule List: {count}
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="error"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportPdf}
              disabled={exporting || loading}
            >
              PDF
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              disabled={exporting || loading || count === 0}
            >
              Excel
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="From Date"
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            label="To Date"
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel variant="standard" htmlFor="dispatch-search-by">
              Search By
            </InputLabel>
            <NativeSelect
              value={searchBy}
              onChange={(e) => setSearchBy(e.target.value)}
              inputProps={{ id: "dispatch-search-by" }}
            >
              <option value="customer_name">Customer</option>
              <option value="schedule_no">Schedule No.</option>
              <option value="order_no">PO No.</option>
              <option value="start_date">Start Date</option>
              <option value="end_date">End Date</option>
              <option value="item_name">Item Name</option>
            </NativeSelect>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <SearchBox search={search} setSearch={setSearch} placeholder="Search" />
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellStyle} />
              <TableCell sx={headerCellStyle}>S.No.</TableCell>
              <TableCell sx={headerCellStyle} sortDirection={orderBy === "schedule_no" ? order : false}>
                <TableSortLabel
                  active={orderBy === "schedule_no"}
                  direction={orderBy === "schedule_no" ? order : "asc"}
                  onClick={() => handleRequestSort("schedule_no")}
                  sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
                >
                  Schedule No.
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellStyle} sortDirection={orderBy === "customer_name" ? order : false}>
                <TableSortLabel
                  active={orderBy === "customer_name"}
                  direction={orderBy === "customer_name" ? order : "asc"}
                  onClick={() => handleRequestSort("customer_name")}
                  sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
                >
                  Customer
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellStyle} sortDirection={orderBy === "start_date" ? order : false}>
                <TableSortLabel
                  active={orderBy === "start_date"}
                  direction={orderBy === "start_date" ? order : "asc"}
                  onClick={() => handleRequestSort("start_date")}
                  sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
                >
                  Start Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellStyle} sortDirection={orderBy === "end_date" ? order : false}>
                <TableSortLabel
                  active={orderBy === "end_date"}
                  direction={orderBy === "end_date" ? order : "asc"}
                  onClick={() => handleRequestSort("end_date")}
                  sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
                >
                  Target Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellStyle}>PO No.</TableCell>
              <TableCell sx={headerCellStyle}>Items</TableCell>
              <TableCell sx={headerCellStyle} sortDirection={orderBy === "created_at" ? order : false}>
                <TableSortLabel
                  active={orderBy === "created_at"}
                  direction={orderBy === "created_at" ? order : "asc"}
                  onClick={() => handleRequestSort("created_at")}
                  sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
                >
                  Created At
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellStyle}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <LoaderNew />
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              rows.map((row, index) => (
                <React.Fragment key={row.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleRow(row.id)}
                        sx={{
                          transform:
                            expandedRowId === row.id ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "0.2s",
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{row.schedule_no || "-"}</TableCell>
                    <TableCell>{row.customer_name}</TableCell>
                    <TableCell>{formatDispatchDate(row.start_date)}</TableCell>
                    <TableCell>{formatDispatchDate(row.end_date)}</TableCell>
                    <TableCell>{row.sales_order_numbers || "-"}</TableCell>
                    <TableCell>{row.items?.length || 0}</TableCell>
                    <TableCell>
                      {row.created_at
                        ? new Date(row.created_at).toLocaleDateString()
                        : ""}
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={(event) => handleMenuClick(event, row)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={11} sx={{ py: 0, borderBottom: 0 }}>
                      <Collapse in={expandedRowId === row.id} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={subHeaderCellStyle}>Item Name</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Item Code</TableCell>
                                <TableCell sx={subHeaderCellStyle}>PO No.</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Quantity</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Rate</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(row.items || []).map((item) => (
                                <TableRow key={item.id}>
                                  <TableCell>{item.itemname}</TableCell>
                                  <TableCell>{item.item_code}</TableCell>
                                  <TableCell>{item.order_no || "-"}</TableCell>
                                  <TableCell>{item.qty}</TableCell>
                                  <TableCell>{item.rate}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ color: "red" }}>
                  No records found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleRowPrint(selectedRow)}>
          <PrintTwoToneIcon sx={{ marginRight: 1 }} color="primary" />
          Print
        </MenuItem>
      </Menu>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[20, 50, 100]}
      />
    </>
  );
};

export default memo(DispatchScheduleTable);
